/**
 * Implementação web do `tirvu-face`: MediaPipe Face Landmarker (wasm + modelo locais) para
 * detecção/landmarks/ângulos, e o MESMO alinhamento do nativo — transformação de
 * similaridade por mínimos quadrados dos 4 pontos (olhos + cantos da boca) para o template
 * ArcFace, amostragem bilinear, fundo preto, luma inteira Rec.601.
 *
 * Landmarks do MediaPipe (malha de 478 pontos): íris 468/473 (centros dos olhos), cantos
 * da boca 61/291, base do nariz 2. Olhos/boca são ordenados por X como no nativo
 * ("left"/"right" = lados da IMAGEM).
 */
import type { FaceLandmarker as TipoFaceLandmarker } from "@mediapipe/tasks-vision";
import { uriParaBlob } from "@/platform/file-system";
import type { Box, DetectResult, DetectedFace, FaceLandmarks, Point, TileSpec } from "./tirvu-face";

const TEMPLATE = {
  leftEye: { x: 38.2946, y: 51.6963 },
  rightEye: { x: 73.5318, y: 51.5014 },
  mouthLeft: { x: 41.5493, y: 92.3655 },
  mouthRight: { x: 70.7299, y: 92.2041 },
};

/** Canvas com o contexto WebGL2 já criado em "low-power" (o MediaPipe reaproveita o mesmo). */
function criarCanvasBaixoConsumo(): HTMLCanvasElement | OffscreenCanvas | undefined {
  try {
    const canvas: HTMLCanvasElement | OffscreenCanvas =
      typeof OffscreenCanvas !== "undefined" ? new OffscreenCanvas(1, 1) : document.createElement("canvas");
    const gl = canvas.getContext("webgl2", { powerPreference: "low-power" } as WebGLContextAttributes);
    return gl ? canvas : undefined;
  } catch {
    return undefined;
  }
}

let landmarker: Promise<TipoFaceLandmarker | null> | null = null;
let indisponivel = false;

export function faceWebDisponivel(): boolean {
  return !indisponivel && typeof createImageBitmap === "function" && typeof WebAssembly !== "undefined";
}

function carregar(): Promise<TipoFaceLandmarker | null> {
  if (!landmarker) {
    landmarker = (async () => {
      const { FaceLandmarker, FilesetResolver } = await import("@mediapipe/tasks-vision");
      const fileset = await FilesetResolver.forVisionTasks("/mediapipe");
      // CPU (XNNPACK) primeiro, e não GPU: com a câmera aberta, a inferência em shaders WebGL
      // derrubava a captura no Windows — a trilha terminava ("Camera was reset") e a webcam
      // SUMIA por um tempo (NotFoundError ao reabrir): o serviço de captura do Chrome divide
      // recursos com o processo da GPU. As fotos do ponto já chegam reduzidas (1080 px), então
      // a CPU dá conta (dezenas de ms no desktop).
      // O MediaPipe ainda usa um WebGL pequeno para receber a imagem: o contexto é criado
      // aqui antes, em "low-power", para não acordar a placa dedicada em notebooks com duas GPUs.
      const canvas = criarCanvasBaixoConsumo();
      const opcoes = (delegate: "GPU" | "CPU") => ({
        baseOptions: { modelAssetPath: "/models/face_landmarker.task", delegate },
        ...(canvas ? { canvas } : null),
        runningMode: "IMAGE" as const,
        numFaces: 3,
        outputFaceBlendshapes: true,
        outputFacialTransformationMatrixes: true,
      });
      try {
        return await FaceLandmarker.createFromOptions(fileset, opcoes("CPU"));
      } catch {
        return await FaceLandmarker.createFromOptions(fileset, opcoes("GPU"));
      }
    })().catch((e) => {
      console.warn("[tirvu-face] MediaPipe indisponível", e);
      indisponivel = true;
      landmarker = null;
      return null;
    });
  }
  return landmarker;
}

/** Pré-carrega o detector (chamado ao abrir a câmera do ponto). */
export function preaquecerFace(): void {
  void carregar();
}

// ─── imagem decodificada (cache entre detect e extractTiles) ────────────────────

type Imagem = { uri: string; largura: number; altura: number; rgba: Uint8ClampedArray; canvas: OffscreenCanvas | HTMLCanvasElement };
let cacheImagem: Imagem | null = null;

function novaTela(w: number, h: number): OffscreenCanvas | HTMLCanvasElement {
  if (typeof OffscreenCanvas !== "undefined") return new OffscreenCanvas(w, h);
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  return c;
}

async function decodificar(uri: string): Promise<Imagem> {
  if (cacheImagem?.uri === uri) return cacheImagem;
  const blob = await uriParaBlob(uri);
  const bmp = await createImageBitmap(blob, { imageOrientation: "from-image" } as ImageBitmapOptions);
  const tela = novaTela(bmp.width, bmp.height);
  const ctx = tela.getContext("2d", { willReadFrequently: true }) as CanvasRenderingContext2D;
  ctx.drawImage(bmp, 0, 0);
  bmp.close();
  const rgba = ctx.getImageData(0, 0, tela.width, tela.height).data;
  cacheImagem = { uri, largura: tela.width, altura: tela.height, rgba, canvas: tela };
  return cacheImagem;
}

// ─── detecção ───────────────────────────────────────────────────────────────────

const grau = (r: number) => (r * 180) / Math.PI;

function angulos(m: number[] | undefined, olhoE: Point, olhoD: Point) {
  // Roll pela linha dos olhos (mesma referência do ML Kit); yaw/pitch pela matriz facial.
  const roll = grau(Math.atan2(olhoD.y - olhoE.y, olhoD.x - olhoE.x));
  if (!m || m.length < 16) return { roll, yaw: 0, pitch: 0 };
  // Matriz 4x4 column-major → rotação.
  const r20 = m[2];
  const r21 = m[6];
  const r22 = m[10];
  const yaw = grau(Math.asin(Math.max(-1, Math.min(1, -r20))));
  const pitch = grau(Math.atan2(r21, r22));
  return { roll, yaw, pitch };
}

export async function detectarWeb(uri: string): Promise<DetectResult> {
  const lm = await carregar();
  if (!lm) throw new Error("TirvuFace indisponível neste navegador");
  const img = await decodificar(uri);
  const r = lm.detect(img.canvas as any);
  const faces: DetectedFace[] = (r.faceLandmarks ?? []).map((pts, i) => {
    const P = (k: number): Point => ({ x: pts[k].x * img.largura, y: pts[k].y * img.altura });
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const p of pts) {
      const x = p.x * img.largura;
      const y = p.y * img.altura;
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
    const box: Box = { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
    const temIris = pts.length > 473;
    const e1 = temIris ? P(468) : P(33);
    const e2 = temIris ? P(473) : P(263);
    const [leftEye, rightEye] = e1.x <= e2.x ? [e1, e2] : [e2, e1];
    const b1 = P(61);
    const b2 = P(291);
    const [mouthLeft, mouthRight] = b1.x <= b2.x ? [b1, b2] : [b2, b1];
    const landmarks: FaceLandmarks = { leftEye, rightEye, mouthLeft, mouthRight, noseBase: P(2) };
    const bs = r.faceBlendshapes?.[i]?.categories ?? [];
    const score = (nome: string) => bs.find((c) => c.categoryName === nome)?.score;
    const piscaE = score("eyeBlinkLeft");
    const piscaD = score("eyeBlinkRight");
    const sorrisoE = score("mouthSmileLeft");
    const sorrisoD = score("mouthSmileRight");
    const ang = angulos(r.facialTransformationMatrixes?.[i]?.data as number[] | undefined, leftEye, rightEye);
    return {
      box,
      landmarks,
      roll: ang.roll,
      yaw: ang.yaw,
      pitch: ang.pitch,
      // Blendshapes são do ponto de vista do sujeito; olho "esquerdo" da imagem = direito do sujeito.
      leftEyeOpen: piscaD != null ? 1 - piscaD : null,
      rightEyeOpen: piscaE != null ? 1 - piscaE : null,
      smiling: sorrisoE != null && sorrisoD != null ? (sorrisoE + sorrisoD) / 2 : null,
      interocularPx: Math.hypot(rightEye.x - leftEye.x, rightEye.y - leftEye.y),
    };
  });
  return { faces, width: img.largura, height: img.altura };
}

// ─── alinhamento ────────────────────────────────────────────────────────────────

/** Similaridade (a, b, tx, ty) com q = [a -b; b a] p + t, por mínimos quadrados. */
function similaridade(src: Point[], dst: Point[]) {
  const n = src.length;
  let sx = 0;
  let sy = 0;
  let dx = 0;
  let dy = 0;
  for (let i = 0; i < n; i++) {
    sx += src[i].x;
    sy += src[i].y;
    dx += dst[i].x;
    dy += dst[i].y;
  }
  sx /= n;
  sy /= n;
  dx /= n;
  dy /= n;
  let num1 = 0;
  let num2 = 0;
  let den = 0;
  for (let i = 0; i < n; i++) {
    const px = src[i].x - sx;
    const py = src[i].y - sy;
    const qx = dst[i].x - dx;
    const qy = dst[i].y - dy;
    num1 += px * qx + py * qy;
    num2 += px * qy - py * qx;
    den += px * px + py * py;
  }
  const a = den ? num1 / den : 1;
  const b = den ? num2 / den : 0;
  return { a, b, tx: dx - (a * sx - b * sy), ty: dy - (b * sx + a * sy) };
}

function amostrar(img: Imagem, x: number, y: number, out: number[]) {
  const W = img.largura;
  const H = img.altura;
  if (x < 0 || y < 0 || x > W - 1 || y > H - 1) {
    out[0] = out[1] = out[2] = 0;
    return;
  }
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const x1 = Math.min(x0 + 1, W - 1);
  const y1 = Math.min(y0 + 1, H - 1);
  const fx = x - x0;
  const fy = y - y0;
  const d = img.rgba;
  for (let c = 0; c < 3; c++) {
    const v00 = d[(y0 * W + x0) * 4 + c];
    const v10 = d[(y0 * W + x1) * 4 + c];
    const v01 = d[(y1 * W + x0) * 4 + c];
    const v11 = d[(y1 * W + x1) * 4 + c];
    out[c] = v00 * (1 - fx) * (1 - fy) + v10 * fx * (1 - fy) + v01 * (1 - fx) * fy + v11 * fx * fy;
  }
}

/** Reduz a imagem por metades até a escala alvo ficar ≤ 2x (antisserrilhado, como o nativo). */
function reduzirPara(img: Imagem, escala: number): { img: Imagem; fator: number } {
  let atual = img;
  let fator = 1;
  while (escala * fator < 0.5 && atual.largura > 64 && atual.altura > 64) {
    const w = Math.max(1, Math.floor(atual.largura / 2));
    const h = Math.max(1, Math.floor(atual.altura / 2));
    const t = novaTela(w, h);
    const c = t.getContext("2d", { willReadFrequently: true }) as CanvasRenderingContext2D;
    c.imageSmoothingQuality = "high";
    c.drawImage(atual.canvas as CanvasImageSource, 0, 0, w, h);
    atual = { uri: "", largura: w, altura: h, rgba: c.getImageData(0, 0, w, h).data, canvas: t };
    fator *= 2;
  }
  return { img: atual, fator };
}

function escrever(saida: Uint8Array, idx: number, rgb: number[], cinza: boolean): number {
  if (cinza) {
    saida[idx] = (77 * rgb[0] + 150 * rgb[1] + 29 * rgb[2] + 128) >> 8;
    return idx + 1;
  }
  saida[idx] = Math.round(rgb[0]);
  saida[idx + 1] = Math.round(rgb[1]);
  saida[idx + 2] = Math.round(rgb[2]);
  return idx + 3;
}

export async function extrairTilesWeb(uri: string, lmk: FaceLandmarks, box: Box, specs: Required<TileSpec>[]): Promise<Uint8Array> {
  const base = await decodificar(uri);
  const total = specs.reduce((s, sp) => s + sp.size * sp.size * (sp.grayscale ? 1 : 3), 0);
  const saida = new Uint8Array(total);
  const rgb = [0, 0, 0];
  let idx = 0;
  for (const sp of specs) {
    const k = sp.size / 112;
    if (sp.mode === "arcface") {
      const src = [lmk.leftEye, lmk.rightEye, lmk.mouthLeft, lmk.mouthRight];
      const dst = [TEMPLATE.leftEye, TEMPLATE.rightEye, TEMPLATE.mouthLeft, TEMPLATE.mouthRight].map((p) => ({ x: p.x * k, y: p.y * k }));
      const escala = Math.hypot(...Object.values(similaridade(src, dst)).slice(0, 2));
      const { img, fator } = reduzirPara(base, escala);
      const srcRed = src.map((p) => ({ x: p.x / fator, y: p.y / fator }));
      const T = similaridade(srcRed, dst);
      // Inversa: p = M^-1 (q - t), M = [a -b; b a].
      const det = T.a * T.a + T.b * T.b || 1;
      for (let v = 0; v < sp.size; v++) {
        for (let u = 0; u < sp.size; u++) {
          const qx = u - T.tx;
          const qy = v - T.ty;
          const px = (T.a * qx + T.b * qy) / det;
          const py = (-T.b * qx + T.a * qy) / det;
          amostrar(img, px, py, rgb);
          idx = escrever(saida, idx, rgb, sp.grayscale);
        }
      }
    } else {
      const lado = Math.max(box.width, box.height) * (1 + 2 * sp.padding);
      const cx = box.x + box.width / 2;
      const cy = box.y + box.height / 2;
      const { img, fator } = reduzirPara(base, sp.size / lado);
      const x0 = (cx - lado / 2) / fator;
      const y0 = (cy - lado / 2) / fator;
      const passo = lado / fator / sp.size;
      for (let v = 0; v < sp.size; v++) {
        for (let u = 0; u < sp.size; u++) {
          amostrar(img, x0 + (u + 0.5) * passo - 0.5, y0 + (v + 0.5) * passo - 0.5, rgb);
          idx = escrever(saida, idx, rgb, sp.grayscale);
        }
      }
    }
  }
  return saida;
}
